import { Location } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import {
	CustomiserNotification,
	Link,
	NotificationState,
	NotificationType
} from '@models/customiser-notification-model';
import { CustomizerParams } from '@models/customizer-params';
import { MiscGroup } from '@models/misc-group';
import { ProductLineModel } from '@models/product-line-model';
import { TempleInclinationEnum } from '@models/variant-temple-inclination';
import { UrlConstants } from '@shared/url-constants';
import { UserTypeConstant } from '@shared/user-type-constants';
import { plainToClass } from 'class-transformer';
import { ModelModel } from '@models/api-models/model';
import { ConfigurationModel } from '@models/configuration';
import { InspirationGroupModel } from '@models/inspiration-group';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeModel } from '@models/model-size';
import { MiscModelCategory } from '../_models/misc-model-category';
import { MiscModelGroup } from '../_models/misc-model-group';
import { OrderItemModel } from '../_models/order-item';
import { ComponentVariantModel } from './../_models/component-variant';
import { AppConstants } from './../app.constants';
import { BrandModeService } from './brandmode.service';
import { BreadcrumbService } from './breadcrumb.service';
import { CustomizerService } from './customizer.service';
import { DataApiService } from './data-api.service';
import { EnduserDisplayService } from './enduserdisplay.service';
import { ExternalSearchService } from './external-search.service';
import { GuidService } from './guid.service';
import { LanguageService } from './language.service';
import { LoginService } from './login.service';
import { OrderOnBehalfService } from './order-on-behalf.service';
import { SessionService } from './session.service';
import { UserService } from './user.service';
import { ModelsService } from './v2/models.service';
import { ModelChangedService } from '@app/customizer/customizer';

@Injectable({
	providedIn: 'root'
})
export class NavigateService {

	modelChangedService = inject(ModelChangedService);
	private lastVersionCheck: number = Date.now();
	private timeToWaitUntilNextCheck: number = 10 * 60 * 1000; // 10 minutes in miliseconds
	private unfinishedOrdersCheckInterval: number = 3 * 24 * 60 * 60 * 1000; // 3 days in miliseconds
	private lastUnfinishedOrdersCheck?: number = Date.now();
	private lastUnfinishedOrdersCheckKey = 'LastUnfinishedOrdersCheck';

	constructor(private router: Router,
				private activatedRoute: ActivatedRoute,
				private sessionService: SessionService,
				private dataApiService: DataApiService,
				private userService: UserService,
				private enduserDisplayService: EnduserDisplayService,
				private translateService: LanguageService,
				private location: Location,
				private externalSearchService: ExternalSearchService,
				private customizerService: CustomizerService,
				private orderOnBehalfService: OrderOnBehalfService,
				private breadcrumbService: BreadcrumbService,
				private loginService: LoginService,
				private brandModeService: BrandModeService,
				private modelsService: ModelsService,
				private guidService: GuidService
	) {
	}

	private set miscCategory(category: MiscModelCategory) {
		this.sessionService.set('miscCategory', category);
	}

	private set miscGroup(group: MiscModelGroup) {
		this.sessionService.set('miscGroup', group);
	}

	private set modelGroupData(modelGroup: ModelGroupModel | null | undefined) {
		this.sessionService.set('modelGroup', modelGroup);
	}

	private set inspirationGroupData(inspirationGroup: InspirationGroupModel | undefined) {
		this.sessionService.set('inspirationGroup', inspirationGroup);
	}

	private set searchTerm(term: string) {
		this.sessionService.set('searchTerm', term);
	}

	public async navigate(commands: Array<string | undefined | null>, extras?: NavigationExtras, parts?: boolean) {
		const currentUser = await this.userService.GetUser();

		if (currentUser?.IsLiteLogin) {
			this.loginService.liteLogout();
			this.loginService.Logout();
			window.location.href = '/login';
		}

		let qp = null;

		if (commands && commands.length > 0 && commands[commands.length - 1]?.includes('?')) {
			let lastCommand = commands[commands.length - 1];
			const q = lastCommand?.substring(lastCommand.indexOf('?'));
			const p = q?.substring(q.indexOf('=') + 1);
			qp = { queryParams: { p: p } };
			commands[commands.length - 1] = lastCommand?.substring(0, lastCommand.indexOf('?'));
		}

		if (!qp && parts) {
			qp = { queryParams: { p: parts } };
		}

		if (currentUser?.LastVersionCheck) {
			this.lastVersionCheck = currentUser.LastVersionCheck;
		}

		if (this.sessionService.get(this.lastUnfinishedOrdersCheckKey)) {
			this.lastUnfinishedOrdersCheck = this.sessionService.get(this.lastUnfinishedOrdersCheckKey);
		} else {
			this.sessionService.set(this.lastUnfinishedOrdersCheckKey, Date.now());
		}
		if (!currentUser?.IsLiteLogin && ((this.lastUnfinishedOrdersCheck ?? Date.now()) + this.unfinishedOrdersCheckInterval) < Date.now()) {
			await this.triggerUnfinishedOrdersNotification();
		}

		if ((this.lastVersionCheck + this.timeToWaitUntilNextCheck) < Date.now()) {
			if (commands[0] === undefined) {
				commands[0] = '/';
			}

			if (qp) {
				await this.router.navigate(commands, qp);
			} else if (extras) {
				await this.router.navigate(commands, extras);
			} else {
				await this.router.navigate(commands);
			}

			this.lastVersionCheck = Date.now();
		} else {
			if (commands[0] === undefined) {
				commands[0] = '/';
			}

			if (qp) {
				await this.router.navigate(commands, qp);
			} else if (extras) {
				await this.router.navigate(commands, extras);
			} else {
				await this.router.navigate(commands);
			}
		}
	}

	public getMiscCategory(): MiscModelCategory | null {
		let miscCategory = this.sessionService.get<MiscModelCategory>('miscCategory');

		if (!miscCategory) {
			const root: ActivatedRoute = this.activatedRoute.root;
			const miscModelCategory = this.breadcrumbService.getParameterFromBreadcrumb(root, UrlConstants.CATEGORYCODE);
			miscCategory = { Category: miscModelCategory, CategoryForDisplay: miscModelCategory, groups: [] };
			this.miscCategory = miscCategory;
		}

		return miscCategory ? plainToClass(MiscModelCategory, miscCategory) : null;
	}

	public getMiscGroup(): MiscModelGroup | null {
		let miscGroup = this.sessionService.get<MiscModelGroup>('miscGroup');

		if (!miscGroup) {
			const root: ActivatedRoute = this.activatedRoute.root;
			const miscModelGroup = this.breadcrumbService.getParameterFromBreadcrumb(root, UrlConstants.GROUPCODE);
			miscGroup = { Group: miscModelGroup, GroupForDisplay: miscModelGroup };
			this.miscGroup = miscGroup;
		}

		return miscGroup ? plainToClass(MiscModelGroup, miscGroup) : null;
	}

	public async setModelGroupData(modelGroup: ModelGroupModel | null) {
		this.modelGroupData = modelGroup;
	}

	public async getModelGroupFromURL(): Promise<ModelGroupModel | null> {
		let modelGroup: ModelGroupModel | null | undefined;

		const root: ActivatedRoute = this.activatedRoute.root;
		const modelGroupCodeFromUrl = this.breadcrumbService.getModelgroupFromBreadcrumb(root);
		const modelGroupNameUrl = this.breadcrumbService.getModelgroupName(root);

		modelGroup = await this.modelsService.getModelGroupFromName(modelGroupCodeFromUrl);

		if (modelGroupNameUrl && modelGroupNameUrl.length > 0) {
			modelGroup = modelGroup?.ModelGroupChildren.find(x => x?.Description?.Fallback == modelGroupNameUrl);
		}

		return modelGroup ? plainToClass(ModelGroupModel, modelGroup) : null;
	}

	public async getModelGroupFromSessionServiceOrUrl(): Promise<ModelGroupModel | null> {
		let modelGroup: ModelGroupModel | null | undefined = this.sessionService.get<ModelGroupModel>('modelGroup');

		if (!modelGroup) {
			const root: ActivatedRoute = this.activatedRoute.root;
			const modelGroupCodeFromUrl = this.breadcrumbService.getModelgroupFromBreadcrumb(root);
			const modelGroupNameUrl = this.breadcrumbService.getModelgroupName(root);

			modelGroup = await this.modelsService.getModelGroupFromName(modelGroupCodeFromUrl);

			if (modelGroupNameUrl && modelGroupNameUrl.length > 0) {
				modelGroup = modelGroup?.ModelGroupChildren.find(x => x?.Description?.Fallback == modelGroupNameUrl);
			}
		}

		return modelGroup ? plainToClass(ModelGroupModel, modelGroup) : null;
	}

	public getInspirationGroupData(): InspirationGroupModel | null {
		const model = this.sessionService.get<InspirationGroupModel>('inspirationGroup');
		return model ? plainToClass(InspirationGroupModel, model) : null;
	}

	public getSearchTerm(): string | undefined {
		const searchTerm = this.sessionService.get<string>('searchTerm');
		return searchTerm;
	}

	public async navigateToTeenList(modelGroup: ModelGroupModel | null) {
		this.modelGroupData = modelGroup;
		const modelGroupName = modelGroup?.ShortDescription?.Fallback || modelGroup?.Description?.Fallback;
		await this.navigate(['kid-teen', 'productline', modelGroupName]);
	}

	public async navigateToSpecialEditionModelGroupList(modelGroup: ModelGroupModel | null) {
		this.modelGroupData = modelGroup;
		const modelGroupName = modelGroup?.ShortDescription?.Fallback || modelGroup?.Description?.Fallback;
		await this.navigate(['special-edition', 'productline', modelGroupName]);
	}

	public async navigateToModelList(modelGroup: ModelGroupModel | null, parentModelGroup?: ModelGroupModel | null, partsMode?: boolean) {
		const user = await this.userService.GetUser();
		this.modelGroupData = modelGroup;
		const modelGroupName = modelGroup?.ShortDescription?.Fallback || modelGroup?.Description?.Fallback;

		if (parentModelGroup) {
			await this.navigate([this.brandModeService.currentBrandStringValue(), 'group', parentModelGroup.ShortDescription?.Fallback || parentModelGroup.Description?.Fallback, modelGroupName, 'models']);
		} else if (modelGroup?.KidTeen) {
			await this.navigate(['kid-teen', modelGroupName, 'models']);
		} else if (modelGroup?.IsRimless && modelGroup.ProductLineCode) {
			const canCustomiseProduct = user?.hasAllProductlines([modelGroup.ProductLineCode]);

			if (!canCustomiseProduct) {
				await this.navigate([this.brandModeService.currentBrandStringValue(), 'group', modelGroupName, 'models']);
			} else {
				if (partsMode) {
					await this.navigateToCustomiserParts(modelGroup);
				} else {
					await this.navigateToCustomizer(modelGroup);
				}
			}
		} else if (modelGroup?.ModelGroupChildren && modelGroup.ModelGroupChildren.length) {
			await this.navigate([this.brandModeService.currentBrandStringValue(), 'group', modelGroupName]);
		} else {
			if (partsMode) {
				await this.navigate([this.brandModeService.currentBrandStringValue(), 'group', modelGroupName, 'models?p=' + partsMode]);
			} else {
				await this.navigate([this.brandModeService.currentBrandStringValue(), 'group', modelGroupName, 'models']);
			}
		}
	}

	async navigateToProductLineModelList(productLine: ProductLineModel, partsMode: boolean = false) {
		let partsModeIndicator: string = '';

		if (partsMode) {
			partsModeIndicator = '?p=true';
		}

		await this.navigate([this.brandModeService.currentBrandStringValue(), 'productline', productLine.Code.toLowerCase() + partsModeIndicator]);
	}

	public async navigateToFeatured(inspirationGroup?: InspirationGroupModel) {
		this.inspirationGroupData = inspirationGroup;
		if (inspirationGroup?.Name)
			await this.navigate(['featured', this.translateService.instant(inspirationGroup.Name)]);
	}

	public async navigateToKidTeen() {
		await this.navigate(['kid-teen']);
	}

	public async navigateToSpecialEdition() {
		await this.navigate(['special-edition']);
	}

	public async navigateToLimitedEditionCNYGoat(modelGroup: ModelGroupModel | null = null) {
		this.modelGroupData = modelGroup;
		const modelGroupName = modelGroup?.ShortDescription?.Fallback || modelGroup?.Description?.Fallback;

		await this.navigate([UrlConstants.LIMITED_EDITION_CNY_GOAT, 'productline', modelGroupName]);
	}

	public async navigateToBag() {
		await this.navigate(['bag']);
	}

	public async navigateToLogin() {
		await this.navigate(['login']);
	}

	public navigateToTechnicalInfo() {
		window.open(AppConstants.technicalInfoUrl, '_blank');
	}

	public async navigateToHome(zone?: NgZone) {
		// When going home we always want to be in default (titanium) mode
		this.brandModeService.setDefaultBrandMode();

		if (zone) {
			await zone.run(async () => {
				await this.navigate(['/']);
			});
		} else {
			await this.navigate(['/']);
		}
	}

	public async navigateToHomeWithSearchModal(zone?: NgZone) {
		await this.navigateToHome(zone);
		const savedSearchTerm = this.getSearchTerm();

		if (savedSearchTerm) {
			this.externalSearchService.search(savedSearchTerm);
		}
	}

	public async navigateToMiscCategory(miscModelCategory: MiscModelCategory) {
		this.miscCategory = miscModelCategory;
		await this.navigate(['accessories', miscModelCategory.CategoryForDisplay]);
	}

	public async navigateToParts() {
		await this.navigate(['parts']);
	}

	public async navigateToKeringEyewareBrands() {
		await this.navigate(['kering-eyeware-brands']);
	}

	public async navigateToLimited() {
		await this.navigate(['limited']);
	}

	public async navigateToCollectors() {
		await this.navigate(['collectors']);
	}

	public async navigateToUnika() {
		await this.navigate(['unika']);
	}

	public async navigateToMiscGroup(miscModelCategory: MiscModelCategory, miscModelGroup: MiscModelGroup) {
		this.miscCategory = miscModelCategory;
		this.miscGroup = miscModelGroup;
		await this.navigate(['accessories', miscModelCategory.CategoryForDisplay, 'group', miscModelGroup.GroupForDisplay]);
	}

	public async navigateToMiscGroup2(group: MiscGroup) {
		if (group.special) {
			if (group.link?.startsWith('http')) {
				window.open(group.link, '_blank');
			} else {
				await this.navigate([group.link]);
			}
		} else {
			await this.navigate(['tools', group.category.name.toLowerCase(), 'group', group.name]);
		}
	}

	public async navigateToMiscGroupByName(miscModelCategory: string, miscModelGroup: string) {
		this.miscCategory = { Category: miscModelCategory, CategoryForDisplay: miscModelCategory, groups: [] };
		this.miscGroup = { Group: miscModelGroup, GroupForDisplay: miscModelGroup };
		await this.navigate(['accessories', miscModelCategory, 'group', miscModelGroup]);
	}

	public async navigateToSpareParts() {
		await this.navigate(['tools', 'parts', 'group', 'parts']);
	}

	public async navigateToCustomiserParts(modelGroup: ModelGroupModel) {
		await this.navigateToCustomizer(modelGroup, undefined, undefined, undefined, undefined, undefined, undefined, undefined, true);
	}

	public async navigateToCustomizer(
		modelGroup?: ModelGroupModel | null,
		model?: ModelModel | null,
		modelsize?: ModelSizeModel,
		templeInclination?: TempleInclinationEnum | null,
		configuration?: ConfigurationModel,
		orderItem?: OrderItemModel,
		allVariants?: Array<ComponentVariantModel>,
		customizerParams?: CustomizerParams,
		parts?: boolean
	): Promise<void> {
		this.customizerService.setCustomizerRouteParams(modelGroup, model, modelsize, templeInclination, orderItem, allVariants, configuration?.ModelConfigurationCode, customizerParams);

		// necessary because of 'HasModelGroupDataGuard'. This navigation method is called directly when entering
		// from a search result (not a manual configuration).
		this.modelGroupData = modelGroup;

		const modelGroupName = modelGroup?.ProductLineCode?.toLowerCase(); //?.Fallback;// || modelGroup?.Description?.Fallback;

		if (orderItem) {
			this.enduserDisplayService.setConfigurationMode(orderItem.Demo, false);
		}

		let options: Array<string | undefined> = [];

		if (model && modelGroup?.ParentModelGroupId) {
			options = [this.brandModeService.currentBrandStringValue(), 'productline', modelGroup.ParentModelGroupShortDescription?.Fallback || modelGroup.ParentModelGroupDescription?.Fallback, modelGroupName, 'model', model.Code];
		} else if (model && modelGroup?.KidTeen) {
			options = ['kid-teen', 'productline', modelGroup?.ShortDescription?.Fallback || modelGroup?.Description?.Fallback, 'model', model.Code];
		} else if (modelGroup?.IsRimless) {
			options = model ? [this.brandModeService.currentBrandStringValue(), 'productline', modelGroupName, 'model', model.Code] : [this.brandModeService.currentBrandStringValue(), 'productline', modelGroupName];
		} else if (model) {
			// options = [this.brandModeService.currentBrandStringValue(), 'group', modelGroupName, 'models', model.Code];
			options = [this.brandModeService.currentBrandStringValue(), 'productline', modelGroup?.ProductLineCode?.toLowerCase(), 'model', model.Code];
		}

		await this.navigate(options, undefined, parts);
	}

	public async navigateToCondensedCustomizer(
		modelGroup: ModelGroupModel,
		model: ModelModel,
		modelsize?: ModelSizeModel,
		templeInclination?: TempleInclinationEnum,
		configuration?: ConfigurationModel,
		orderItem?: OrderItemModel,
		searchTerm?: string,
		inspirationGroup?: InspirationGroupModel,
		customizerParams?: CustomizerParams
	) {
		this.customizerService.setCustomizerRouteParams(modelGroup, model, modelsize, templeInclination, orderItem, undefined, configuration?.ModelConfigurationCode, customizerParams);

		if (searchTerm) {
			this.searchTerm = searchTerm;
		}

		if (inspirationGroup) {
			this.inspirationGroupData = inspirationGroup;
		}

		if (orderItem) {
			this.enduserDisplayService.setConfigurationMode(orderItem.Demo);
		}

		//await this.navigate(['searchresults', modelGroup.Code], { skipLocationChange: false, replaceUrl: true, onSameUrlNavigation: 'reload' });
		await this.navigate(['searchresults', modelGroup.Code]);

	}

	public async UpdateUrl(oldModelCode: string, newModelCode: string): Promise<void> {
		const url = this.location.path().replace(oldModelCode, newModelCode);
		this.location.go(url);

		this.modelChangedService.modelChanged.next(newModelCode);
	}

	// Note: If the "upgrade your LINDBERG collection" bug appears again, look here.
	public async canNavigateToCustomizer() {
		const modelgroup = await this.getModelGroupFromSessionServiceOrUrl();

		if (!modelgroup) {
			return false;
		}

		const user = await this.userService.GetUser();
		const customer = this.orderOnBehalfService.customerSnapshot();

		let canCustomiseProduct: boolean | undefined = undefined;

		if (user?.IsDistributor && customer) {
			return true;
		}

		if (user?.Type === UserTypeConstant.Customer && customer) {
			canCustomiseProduct = customer.hasProductLineAccess(modelgroup.ProductLineCode);
			return canCustomiseProduct;
		}

		if (modelgroup.ProductLineCode)
			canCustomiseProduct = user?.hasAllProductlines([modelgroup.ProductLineCode]);

		return canCustomiseProduct;
	}

	public async canNavigateToCustomizerWithModelgroup(modelgroup: ModelGroupModel | null) {
		const user = await this.userService.GetUser();

		if (!modelgroup) {
			return false;
		}
		if (modelgroup.ProductLineCode) {
			const canCustomiseProduct = user?.hasAllProductlines([modelgroup.ProductLineCode]);

			return canCustomiseProduct;

		}
		return null;
	}

	/**
	 * @deprecated The method should not be used
	 */
	private async triggerUnfinishedOrdersNotification() {
		const freshUnfinishedOrders = await this.dataApiService.getUnfinishedOrders(true);
		const currentUser = await this.userService.GetUser();
		const unfinishedOrdersCount = freshUnfinishedOrders?.length ?? 0;

		if (unfinishedOrdersCount > 0) {
			const customiserNotification = new CustomiserNotification(
				this.guidService.generateGUID().toString(),
				currentUser?.Uuid,
				this.translateService.instant('NOTIFICATIONS.HEADER.UNFINISHED.ORDERS'),
				(unfinishedOrdersCount === 1
					? this.translateService.instant('NOTIFICATIONS.BODY.UNFINISHED.ORDER').replace('{count}', unfinishedOrdersCount.toString())
					: this.translateService.instant('NOTIFICATIONS.BODY.UNFINISHED.ORDERS').replace('{count}', unfinishedOrdersCount.toString())) + ' ' + this.translateService.instant('NOTIFICATIONS.BODY.CLICK.TO.VIEW'),
				NotificationState.New,
				NotificationType.UnfinishedOrders
			);

			customiserNotification.Link = new Link('/bag/order-info', this.translateService.instant('NOTIFICATIONS.LINKTEXT.VIEW.ORDERS'));
		}

		this.lastUnfinishedOrdersCheck = Date.now();
		this.sessionService.set(this.lastUnfinishedOrdersCheckKey, this.lastUnfinishedOrdersCheck);
	}

}
